/**
 * Section / subsection heading for the Progress Note.
 *
 * Renders the heading and registers its anchor with the Table of Contents, so
 * a heading can never exist without a working ToC link (or leave a dead one
 * behind when its section hides itself). Trailing content — the LOINC chip —
 * is passed as `children` so each call site keeps its own styling.
 */
import type { ReactNode } from 'react'
import { Text, type MantineSpacing, type TextProps } from '@mantine/core'
import { useTocAnchor, type TocLevel } from '../../ProgressNoteViewer/tocRegistry'
import { SUBSECTION_LABEL_COLOR } from '../tokens'

const STYLES: Record<TocLevel, TextProps & { mb: MantineSpacing }> = {
  section: { fw: 700, size: 'lg', c: 'dark.6', mb: 'sm' },
  sub: { fw: 700, size: 'sm', c: SUBSECTION_LABEL_COLOR, mb: 'xs' },
}

type NoteHeadingProps = {
  level: TocLevel
  id: string
  label: string
  /** Overrides the level default — for headings whose wrapper owns the gap. */
  mb?: MantineSpacing
  children?: ReactNode
}

export function NoteHeading({ level, id, label, mb, children }: NoteHeadingProps) {
  useTocAnchor(id)
  const style = STYLES[level]

  return (
    <Text {...style} mb={mb ?? style.mb} id={id}>
      {label}
      {children ? ' ' : null}
      {children}
    </Text>
  )
}
